import {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { logger as baseLogger } from '../logger';

const ALLOWED_HEADERS = new Set([
  'x-request-id',
  'x-coubic-locale',
  'x-coubic-request-host',
  'api-env',
  'uatype',
  'x-merchant-public-id',
]);

const sanitizeHeaders = (
  headers: AxiosRequestConfig['headers'] | undefined,
) => {
  if (!headers || typeof headers !== 'object') {
    return undefined;
  }

  const headerEntries = Object.entries(headers as Record<string, unknown>);
  if (headerEntries.length === 0) {
    return undefined;
  }

  const sanitizedHeaders: Record<string, unknown> = {};

  headerEntries.forEach(([key, value]) => {
    if (
      ALLOWED_HEADERS.has(key.toLowerCase()) &&
      typeof value !== 'undefined'
    ) {
      sanitizedHeaders[key] = value;
    }
  });

  return Object.keys(sanitizedHeaders).length > 0
    ? sanitizedHeaders
    : undefined;
};

type LoggableRequest = {
  method?: string;
  url?: string | null;
  headers?: Record<string, unknown>;
  params?: Record<string, unknown>;
};

type AxiosLogContext = {
  request: LoggableRequest;
  startTime: number;
};

type LoggedAxiosRequestConfig = AxiosRequestConfig & {
  __axiosLogContext?: AxiosLogContext;
};

const buildRequestUrl = (config: AxiosRequestConfig) => {
  const { baseURL, url } = config;
  if (!url) {
    return null;
  }
  if (!baseURL) {
    return url;
  }
  try {
    return new URL(url, baseURL).toString();
  } catch {
    return `${baseURL}${url}`;
  }
};

const buildAxiosRequestLog = (config: AxiosRequestConfig): LoggableRequest => {
  const params =
    config.params && typeof config.params === 'object'
      ? (config.params as Record<string, unknown>)
      : undefined;

  return {
    method: config.method?.toUpperCase(),
    url: buildRequestUrl(config),
    headers: sanitizeHeaders(config.headers),
    params,
  };
};

const buildErrorDetail = (error: AxiosError) => ({
  message: error.message,
  code: error.code,
});

const buildAxiosLogPayload = (
  context: AxiosLogContext | undefined,
  response: { statusCode?: number },
  error?: AxiosError,
) => {
  const durationMs = context ? Date.now() - context.startTime : undefined;

  const payload: {
    request?: LoggableRequest;
    response?: { statusCode?: number; durationMs?: number };
    error?: {
      message: string;
      code?: string;
    };
  } = {};

  if (context?.request) {
    payload.request = context.request;
  }

  if (
    typeof response.statusCode !== 'undefined' ||
    typeof durationMs !== 'undefined'
  ) {
    payload.response = {
      statusCode: response.statusCode,
      durationMs,
    };
  }

  if (error) {
    payload.error = buildErrorDetail(error);
  }

  return payload;
};

export class SecureAxiosLogger {
  private logger: typeof baseLogger;

  constructor(bindings: Record<string, unknown>) {
    this.logger = baseLogger.child(bindings);
  }

  public attach(axiosInstance: AxiosInstance) {
    axiosInstance.interceptors.request.use((config) => {
      const typedConfig = config as LoggedAxiosRequestConfig;
      typedConfig.__axiosLogContext = {
        request: buildAxiosRequestLog(config),
        startTime: Date.now(),
      };
      return typedConfig;
    });

    axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        const typedConfig = response.config as LoggedAxiosRequestConfig;
        const payload = buildAxiosLogPayload(typedConfig.__axiosLogContext, {
          statusCode: response.status,
        });
        this.logger.info(payload, 'API request completed');
        return response;
      },
      (error: AxiosError) => {
        const typedConfig = (error.config || {}) as LoggedAxiosRequestConfig;
        const statusCode = error.response?.status;
        const payload = buildAxiosLogPayload(
          typedConfig.__axiosLogContext,
          { statusCode },
          error,
        );

        const shouldTreatAsWarning =
          typeof statusCode === 'number' &&
          statusCode >= 400 &&
          statusCode < 500;
        if (shouldTreatAsWarning) {
          this.logger.warn(payload, 'API request failed');
        } else {
          this.logger.error(payload, 'API request failed');
        }

        throw error;
      },
    );
  }
}
